import React, {useMemo, useId} from 'react'
import {List, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/cms/module-container'
import Heading from '../../components/cms/heading'
import Fade from '../../components/cms/fade'
import {cardsSlotRecipe} from './index.recipe'
import CardItem from './card-item'
import Slider from '../../components/cms/slider'

const Cards = React.forwardRef(function Cards(
    {
        moduleId,
        moduleType,
        pageId,
        headingLevel,
        heading,
        anchor,
        background,
        variantCards,
        cards,
        spacing,
        modulePadding = 'default',
        mobileLayout = 'vertical'
    },
    ref
) {
    const recipe = useSlotRecipe({recipe: cardsSlotRecipe})
    const styles = useMemo(
        () => recipe({variant: variantCards, count: String(cards.length)}), // count must be a string to match the recipe
        [cards.length, recipe, variantCards]
    )

    const sliderId = useId()
    // We show it if variant is 'usp' OR if the CMS option 'horizontal' is selected
    const isSliderActive =
        mobileLayout === 'horizontal' || variantCards === 'usp' || variantCards === 'uspSummaryStep'

    const sliderVariant = variantCards === 'uspSummaryStep' ? 'uspSummaryStep' : 'horizontalCards'

    const sliderLabel = useMemo(() => {
        return [heading?.subline && `${heading.subline} -`, heading?.headline1, heading?.headline2]
            .filter(Boolean)
            .join(' ')
    }, [heading])

    const cardItems = cards.map((card, idx) => (
        <CardItem
            key={card.id || idx}
            card={card}
            headingLevel={headingLevel}
            styles={styles}
            variant={variantCards}
            trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
        />
    ))

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            ref={ref}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            modulePadding={modulePadding}
            {...styles.moduleContainer}
        >
            {heading && (
                <Fade>
                    <Heading
                        {...heading}
                        headingLevel={headingLevel}
                        alignment={{base: 'left', s: 'center'}}
                    />
                </Fade>
            )}

            {isSliderActive ? (
                <List.Root variant="noIndicator" {...styles.cards}>
                    <Slider variant={sliderVariant} sliderId={sliderId} sliderLabel={sliderLabel}>
                        {cardItems}
                    </Slider>
                </List.Root>
            ) : (
                <List.Root variant="noIndicator" {...styles.cards}>
                    {cardItems}
                </List.Root>
            )}
        </ModuleContainer>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Cards.displayName = 'Cards'
}

Cards.propTypes = {
    headingLevel: PropTypes.number,
    heading: PropTypes.object,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    anchor: PropTypes.string,
    variantCards: PropTypes.oneOf(['default', 'condensed', 'with-overlay', 'usp']),
    cards: PropTypes.array,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom', 'none']),
    modulePadding: PropTypes.string,
    mobileLayout: PropTypes.oneOf(['vertical', 'horizontal'])
}

export default Cards
